import { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import type { AdminOrgManagementQuery } from 'src/generated/graphql';
import { useAdminUnbanUserMutation } from 'src/generated/graphql';

type BannedEmail = NonNullable<
  AdminOrgManagementQuery['adminDashboard']
>['bannedEmails'][0];

interface BannedEmailsListProps {
  emails: BannedEmail[];
  onUnban: () => void;
}

export const BannedEmailsList = ({ emails, onUnban }: BannedEmailsListProps) => {
  const [unbanningEmail, setUnbanningEmail] = useState<string | null>(null);

  const [unbanUser, { loading: unbanLoading }] = useAdminUnbanUserMutation({
    onCompleted: () => {
      setUnbanningEmail(null);
      onUnban();
    },
  });

  const handleUnban = (email: string) => {
    setUnbanningEmail(email);
    unbanUser({ variables: { email } });
  };

  if (emails.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center text-gray-500">
        No banned emails.
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Email
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Reason
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Banned
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Banned By
            </th>
            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {emails.map((bannedEmail) => (
            <tr key={bannedEmail.id} className="hover:bg-gray-50">
              <td className="px-4 py-3 whitespace-nowrap">
                <div className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-red-500" />
                  <span className="text-sm font-mono text-gray-900">
                    {bannedEmail.email}
                  </span>
                </div>
              </td>
              <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate">
                {bannedEmail.reason || '-'}
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                {bannedEmail.bannedAt
                  ? formatDistanceToNow(new Date(bannedEmail.bannedAt), {
                      addSuffix: true,
                    })
                  : '-'}
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 font-mono">
                {bannedEmail.bannedBy
                  ? bannedEmail.bannedBy.slice(0, 8) + '...'
                  : '-'}
              </td>
              <td className="px-4 py-3 whitespace-nowrap text-right">
                <button
                  onClick={() => handleUnban(bannedEmail.email)}
                  disabled={unbanLoading && unbanningEmail === bannedEmail.email}
                  className="px-3 py-1.5 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-50 text-xs font-medium"
                >
                  {unbanLoading && unbanningEmail === bannedEmail.email
                    ? 'Unbanning...'
                    : 'Unban'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
